import { useEffect, useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer,
} from "recharts";
import { ChevronDown } from "lucide-react";

/* theme-aware palette (site has dark + light themes) */
const PALETTES = {
  dark: {
    grid: "#232c44", fg: "#f4f7fb", muted: "#8b95ab", track: "#232c44",
    cyan: "#22d3ee", violet: "#8b5cf6", green: "#4ade80", amber: "#fbbf24", red: "#f87171",
  },
  light: {
    grid: "#dbe3f0", fg: "#0f172a", muted: "#64748b", track: "#e2e8f0",
    cyan: "#0e7490", violet: "#6d28d9", green: "#15803d", amber: "#b45309", red: "#dc2626",
  },
};

export const usePalette = () => {
  const [light, setLight] = useState(
    () => typeof document !== "undefined" && document.documentElement.classList.contains("light"),
  );
  useEffect(() => {
    const obs = new MutationObserver(() =>
      setLight(document.documentElement.classList.contains("light")),
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);
  return PALETTES[light ? "light" : "dark"];
};

const tierColor = (p, score) =>
  score >= 80 ? p.green : score >= 60 ? p.cyan : score >= 40 ? p.amber : p.red;

/* ---------- 1. Marker Age semicircular dial ---------- */
export const MarkerDial = ({ age, markerAge, delta, testId = "marker-dial" }) => {
  const p = usePalette();
  const reduced = useReducedMotion();
  const lo = 18, hi = 100;
  const pct = (v) => (Math.min(Math.max(v, lo), hi) - lo) / (hi - lo);
  const fillPct = pct(markerAge);
  const ageAngle = 180 - pct(age) * 180;
  const rad = (ageAngle * Math.PI) / 180;
  const R = 90;
  const tx1 = (R - 12) * Math.cos(rad), ty1 = (R - 12) * Math.sin(rad);
  const tx2 = (R + 14) * Math.cos(rad), ty2 = (R + 14) * Math.sin(rad);
  const good = delta <= 0;
  const chip = delta < 0 ? `${Math.abs(delta)} years younger` : delta === 0 ? "Matches your age" : `${delta} years above your age`;

  return (
    <div data-testid={testId} className="flex flex-col items-center">
      <svg viewBox="-110 -100 220 130" className="w-full max-w-md" role="img" aria-label={`Marker Age ${markerAge}, your age ${age}`}>
        <path d="M -90 0 A 90 90 0 0 1 90 0" fill="none" stroke={p.track} strokeWidth="14" strokeLinecap="round" />
        <motion.path
          d="M -90 0 A 90 90 0 0 1 90 0"
          fill="none" stroke={good ? p.cyan : p.amber} strokeWidth="14" strokeLinecap="round"
          strokeDasharray={`${Math.PI * 90}`}
          initial={{ strokeDashoffset: Math.PI * 90 }}
          animate={{ strokeDashoffset: Math.PI * 90 * (1 - fillPct) }}
          transition={reduced ? { duration: 0 } : { duration: 1.6, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
        />
        <line x1={-tx1} y1={-ty1} x2={-tx2} y2={-ty2} stroke={p.violet} strokeWidth="4" strokeLinecap="round" />
        <text x={Math.max(-88, Math.min(88, -tx2))} y={-ty2 - 8} textAnchor="middle" fill={p.violet} fontSize="11" fontWeight="600">
          Your age: {age}
        </text>
        <text x="0" y="-30" textAnchor="middle" fill={p.fg} fontSize="52" fontWeight="800" className="font-display">
          {markerAge}
        </text>
        <text x="0" y="-8" textAnchor="middle" fill={p.muted} fontSize="12">Your Marker Age</text>
      </svg>
      <span
        data-testid="marker-delta-chip"
        className={`mt-1 rounded-full px-4 py-1.5 text-sm font-semibold ${
          good ? "bg-green-400/10 text-green-600 dark:text-green-300" : "bg-amber-300/10 text-amber-600 dark:text-amber-300"
        }`}
      >
        {chip}
      </span>
    </div>
  );
};

/* ---------- 2. Mental Fitness ring ---------- */
export const FitnessRing = ({ score, tier, testId = "fitness-ring" }) => {
  const p = usePalette();
  const reduced = useReducedMotion();
  const R = 80;
  const C = 2 * Math.PI * R;
  return (
    <div data-testid={testId} className="flex flex-col items-center">
      <svg viewBox="0 0 200 200" className="w-52" role="img" aria-label={`Mental Fitness ${score} of 100, ${tier}`}>
        <circle cx="100" cy="100" r={R} fill="none" stroke={p.track} strokeWidth="16" />
        <motion.circle
          cx="100" cy="100" r={R} fill="none" stroke={p.violet} strokeWidth="16" strokeLinecap="round"
          strokeDasharray={C}
          initial={{ strokeDashoffset: C }}
          animate={{ strokeDashoffset: C * (1 - score / 100) }}
          transition={reduced ? { duration: 0 } : { duration: 1.4, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          transform="rotate(-90 100 100)"
        />
        <text x="100" y="96" textAnchor="middle" fill={p.fg} fontSize="44" fontWeight="800">{score}</text>
        <text x="100" y="124" textAnchor="middle" fill={p.muted} fontSize="12">Mental Fitness</text>
      </svg>
      <span className="mt-1 rounded-full bg-violet-400/10 px-4 py-1.5 text-sm font-semibold text-violet-700 dark:text-violet-300">{tier}</span>
    </div>
  );
};

/* ---------- 3/4. Scan radars (Recharts, site chart lib) ---------- */
export const ScanRadar = ({ axes, scan, testId }) => {
  const p = usePalette();
  const color = scan === "health" ? p.cyan : p.violet;
  const data = useMemo(
    () => axes.map((a) => ({ name: a.name, you: a.score, reference: 60 })),
    [axes],
  );
  return (
    <div data-testid={testId} className="w-full">
      <ResponsiveContainer width="100%" height={380}>
        <RadarChart data={data} outerRadius="72%">
          <PolarGrid stroke={p.grid} gridType="polygon" radialLines />
          <PolarAngleAxis dataKey="name" tick={{ fill: p.muted, fontSize: 11 }} />
          <Radar dataKey="reference" stroke={p.muted} strokeDasharray="5 4" fill="none" strokeWidth={1.4} isAnimationActive={false} />
          <Radar dataKey="you" stroke={color} fill={color} fillOpacity={0.25} strokeWidth={2.4} isAnimationActive />
        </RadarChart>
      </ResponsiveContainer>
      <p className="mt-1 text-center text-xs text-fog">Dashed ring: Global reference 60 (model-based)</p>
    </div>
  );
};

/* ---------- 5. Life ruler ---------- */
export const LifeRuler = ({ d, testId = "life-ruler" }) => {
  const p = usePalette();
  const marks = [
    { x: d.age, label: "Today (you)", color: p.violet, strong: true },
    { x: d.world_healthy_life_expectancy, label: "World healthy life expectancy 61.9", color: p.muted },
    { x: d.world_life_expectancy, label: "World life expectancy 73.3", color: p.muted },
    { x: d.baseline, label: "Average person your age and sex", color: p.muted },
    { x: d.projected, label: "Your projection", color: p.cyan, strong: true },
  ];
  return (
    <div data-testid={testId} className="w-full">
      <div className="relative h-60 sm:h-48">
        <div className="absolute left-0 right-0 top-1/2 h-2 -translate-y-1/2 rounded-full" style={{ background: p.track }} />
        <div
          className="absolute top-1/2 h-2 -translate-y-1/2 rounded-full"
          style={{ left: `${d.range_low}%`, width: `${d.range_high - d.range_low}%`, background: `${p.cyan}44` }}
        />
        {marks.map((m, i) => {
          const above = i % 2 === 0;
          return (
            <div key={m.label} className="absolute top-1/2" style={{ left: `${Math.min(Math.max(m.x, 3), 94)}%` }}>
              <div
                className={`w-0.5 rounded ${m.strong ? "h-7" : "h-5"}`}
                style={{ background: m.color, transform: "translate(-50%,-50%)" }}
              />
              <div
                className={`absolute w-28 -translate-x-1/2 text-[11px] leading-tight sm:w-32 ${
                  above ? "bottom-full mb-4 sm:mb-5" : "top-full mt-4 sm:mt-5"
                } ${i >= 3 ? "hidden sm:block" : ""}`}
                style={{ color: m.strong ? p.fg : p.muted, fontWeight: m.strong ? 700 : 400 }}
              >
                {m.label}
              </div>
            </div>
          );
        })}
      </div>
      <ul className="mt-1 space-y-1 text-[11px] text-fog sm:hidden">
        <li>Average person your age and sex worldwide: {d.baseline}</li>
        <li>Your projection: {d.projected} (likely {d.range_low} to {d.range_high})</li>
      </ul>
      <div className="mt-2 flex justify-between text-[10px] text-fog opacity-60">
        <span>0</span><span>25</span><span>50</span><span>75</span><span>100 yrs</span>
      </div>
    </div>
  );
};

/* ---------- 6. Factor bars with expandable explanations ---------- */
export const FactorBars = ({ axes, testId = "factor-bars" }) => {
  const p = usePalette();
  const reduced = useReducedMotion();
  const [open, setOpen] = useState(null);
  return (
    <div data-testid={testId} className="space-y-3">
      {axes.map((a, i) => (
        <div key={a.id} className="rounded-2xl border border-white/10 bg-surface p-4">
          <button
            type="button"
            data-testid={`factor-${a.id.toLowerCase()}`}
            onClick={() => setOpen(open === a.id ? null : a.id)}
            className="flex w-full items-center gap-3 text-left"
            aria-expanded={open === a.id}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-medium">{a.name}</span>
                <span className="text-sm font-bold" style={{ color: tierColor(p, a.score) }}>{a.score}</span>
              </div>
              <div className="mt-2 h-2 overflow-hidden rounded-full" style={{ background: p.track }}>
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: tierColor(p, a.score) }}
                  initial={{ width: 0 }}
                  whileInView={{ width: `${a.score}%` }}
                  viewport={{ once: true }}
                  transition={reduced ? { duration: 0 } : { duration: 0.9, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>
            </div>
            <span className="shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide"
              style={{ background: `${tierColor(p, a.score)}1f`, color: tierColor(p, a.score) }}>
              {a.tier}
            </span>
            <ChevronDown className={`h-4 w-4 shrink-0 text-fog transition-transform duration-300 ${open === a.id ? "rotate-180" : ""}`} />
          </button>
          {open === a.id && (
            <div className="mt-3 space-y-2 border-t border-white/10 pt-3 text-sm leading-relaxed text-fog">
              <p><strong className="font-semibold text-inherit">What it is:</strong> {a.copy.what_it_is}</p>
              <p><strong className="font-semibold text-inherit">Why it matters for longevity:</strong> {a.copy.why_it_matters}</p>
              <p><strong className="font-semibold text-inherit">What your answer suggests:</strong> {a.copy.what_your_answer_suggests}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

/* ---------- 7. Position bell curve ---------- */
export const BellCurve = ({ li, percentile, testId = "position-curve" }) => {
  const p = usePalette();
  const W = 560, H = 200, x0 = 30, x1 = W - 30;
  const xFor = (v) => x0 + ((v - 5) / 90) * (x1 - x0);
  const pts = [];
  for (let x = 5; x <= 95; x += 1) {
    const y = Math.exp(-0.5 * ((x - 60) / 15) ** 2);
    pts.push([xFor(x), H - 24 - y * (H - 60)]);
  }
  const path = `M ${pts.map((pt) => pt.map((n) => n.toFixed(1)).join(" ")).join(" L ")}`;
  const px = xFor(Math.min(Math.max(li, 5), 95));
  return (
    <div data-testid={testId} className="flex flex-col items-center">
      <svg viewBox={`0 0 ${W} ${H + 30}`} className="w-full max-w-xl" role="img" aria-label={`Position curve, higher than about ${percentile} in 100`}>
        <path d={path} fill="none" stroke={p.cyan} strokeWidth="2.4" />
        <path d={`${path} L ${x1} ${H - 24} L ${x0} ${H - 24} Z`} fill={p.cyan} fillOpacity="0.10" stroke="none" />
        <line x1={px} y1={24} x2={px} y2={H - 24} stroke={p.violet} strokeWidth="2.5" />
        <text x={px} y={16} textAnchor="middle" fill={p.violet} fontSize="13" fontWeight="700">You are here</text>
        <line x1={x0} y1={H - 24} x2={x1} y2={H - 24} stroke={p.track} strokeWidth="2" />
      </svg>
      <p className="text-sm text-fog">
        Higher than about {percentile} in 100. Estimated position on a global reference curve (model-based).
      </p>
    </div>
  );
};

/* ---------- Sample animated radar (hero / promo) ---------- */
export const SampleRadar = ({ className = "h-24 w-24", variant }) => {
  const p = usePalette();
  const green = variant === "green";
  const gridColor = green
    ? (p.fg === "#f4f7fb" ? "rgba(255,255,255,0.35)" : "rgba(15,23,42,0.25)")
    : p.grid;
  const polyColor = green ? p.green : p.cyan;
  const reduced = useReducedMotion();
  const cx = 70, cy = 70, R = 56;
  const hex = (r) =>
    Array.from({ length: 6 }, (_, i) => {
      const a = (Math.PI / 3) * i - Math.PI / 2;
      return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
    }).join(" ");
  const shape = [0.85, 0.62, 0.9, 0.5, 0.74, 0.66].map((v, i) => {
    const a = (Math.PI / 3) * i - Math.PI / 2;
    return `${cx + R * v * Math.cos(a)},${cy + R * v * Math.sin(a)}`;
  }).join(" ");
  return (
    <svg viewBox="0 0 140 140" className={className} aria-hidden="true">
      {[R, R * 0.66, R * 0.33].map((r) => (
        <polygon key={r} points={hex(r)} fill="none" stroke={gridColor} strokeWidth="1" />
      ))}
      <motion.polygon
        points={shape} fill={polyColor} fillOpacity="0.25" stroke={polyColor} strokeWidth="2"
        initial={reduced ? false : { scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        style={{ transformOrigin: "70px 70px" }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], repeat: Infinity, repeatType: "reverse", repeatDelay: 1.6 }}
      />
    </svg>
  );
};
